// Zod DTOs for the `/v1/sections` authoring surface (spec §3.3 `Section`
// model — the self-referencing chapter/unit/topic tree, `depth` enforced
// <= 2, materialised `path`; §6.2 "Tree mutation semantics"). `subjectId` is
// a path param on create (`POST /v1/subjects/:id/sections`); `depth`/`path`/
// `position`/`isPublished`/`publishedAt` are all server-computed, never
// accepted in a request body.
import { z } from 'zod';
import { SectionKind } from '../enums';
import {
  idSchema,
  positionSchema,
  reorderRequestSchema,
  slugSchema,
  titleSchema,
} from './primitives';

// Exported (not just module-local) so `sectionResponseSchema`/downstream
// consumers (P1-06b/P1-07, the OpenAPI doc) can reuse the exact same enum
// schema rather than re-deriving an equivalent-but-drifting one.
export const sectionKindEnumSchema = z.enum(
  Object.values(SectionKind) as [SectionKind, ...SectionKind[]],
);

const summarySchema = z.string().trim().max(2000).optional();

// ─── POST /v1/subjects/:id/sections ─────────────────────────────────────────
// `parentId` is nullable (root-level chapter under the subject) but, when
// provided, must be a real id — `.nullable().optional()` lets a client omit
// the field entirely (defaults to root) OR send `null` explicitly for the
// same effect.

export const sectionCreateRequestSchema = z.object({
  parentId: idSchema.nullable().optional(),
  kind: sectionKindEnumSchema,
  title: titleSchema,
  slug: slugSchema.optional(),
  summary: summarySchema,
});
export type SectionCreateRequest = z.infer<typeof sectionCreateRequestSchema>;

// ─── PATCH /v1/sections/:id ──────────────────────────────────────────────────
// Deliberately excludes `parentId` — reparenting is the dedicated `move`
// endpoint below (atomic `path`/`depth` rewrite), never a plain field patch.

export const sectionUpdateRequestSchema = z.object({
  kind: sectionKindEnumSchema.optional(),
  title: titleSchema.optional(),
  slug: slugSchema.optional(),
  summary: summarySchema,
});
export type SectionUpdateRequest = z.infer<typeof sectionUpdateRequestSchema>;

// ─── POST /v1/sections/:id/move ─────────────────────────────────────────────
// "{ parentId, position } — atomic reparent" (spec §6.2). `parentId: null`
// moves the section to root (depth 0) within its subject; the service layer
// rejects depth > 2 (`422 SECTION_DEPTH_EXCEEDED`) and moving under one's own
// descendant (`422 SECTION_CYCLE`) — both are runtime/business-rule checks,
// not expressible in the static shape here.

export const sectionMoveRequestSchema = z.object({
  parentId: idSchema.nullable(),
  position: positionSchema,
});
export type SectionMoveRequest = z.infer<typeof sectionMoveRequestSchema>;

// ─── POST /v1/sections/reorder (bulk) ────────────────────────────────────────

export const sectionReorderRequestSchema = reorderRequestSchema;
export type SectionReorderRequest = z.infer<typeof sectionReorderRequestSchema>;

// ─── POST /v1/sections/:id/publish ───────────────────────────────────────────
// `{ cascade: bool }` (spec §6.2) — cascade publishes the whole subtree
// (tabs + resources) in one call; omitting it means "leave descendants'
// publish state untouched", so `cascade` defaults to `false`.

export const sectionPublishRequestSchema = z.object({
  cascade: z.boolean().default(false),
});
export type SectionPublishRequest = z.infer<typeof sectionPublishRequestSchema>;

// ─── POST /v1/sections/:id/unpublish (P1-06b) ────────────────────────────
// Same optional `{ cascade }` shape as publish — "`{cascade}` and
// `/unpublish`" (spec §6.2 AC phrasing groups both verbs under the one
// flag). Cascade un-publishes the whole subtree in one call; omitting it
// leaves descendants' own `isPublished` untouched (the read-time ancestor
// gate — see `SectionsService.isEffectivelyPublished` — already hides them
// regardless, per spec §4 "An unpublished ancestor hides its whole subtree,
// regardless of child publish state").

export const sectionUnpublishRequestSchema = sectionPublishRequestSchema;
export type SectionUnpublishRequest = z.infer<typeof sectionUnpublishRequestSchema>;

// ─── POST /v1/sections/:id/duplicate (P1-06b) ────────────────────────────
// No request body (spec §6.2: "deep copy incl. tabs & resources", no
// documented input shape) — the response is a 202 "accepted/job" envelope,
// never an inline deep copy (ADR-002: CPU-heavy, runs on a Queue). `jobId`
// doubles as the queued message's own idempotency key (see
// `@eskayscience/shared/jobs/section-duplicate.ts`'s header) — a client that
// wants to correlate "did my duplicate finish" can re-fetch the source
// section's parent's `/tree` and look for a new sibling; there is no
// dedicated job-status polling endpoint in this phase's scope.

export const sectionDuplicateAcceptedResponseSchema = z.object({
  jobId: z.string().min(1),
  sourceSectionId: idSchema,
  status: z.literal('queued'),
});
export type SectionDuplicateAcceptedResponse = z.infer<
  typeof sectionDuplicateAcceptedResponseSchema
>;

// ─── Responses (server-computed shapes, P1-06a) ─────────────────────────────
// Every mutating request above accepts none of `position`/`depth`/`path`/
// `isPublished`/`publishedAt` — this is where a caller actually SEES them,
// server-computed from the create/move/reorder services.

export const sectionResponseSchema = z.object({
  id: idSchema,
  subjectId: idSchema,
  parentId: idSchema.nullable(),
  kind: sectionKindEnumSchema,
  title: titleSchema,
  slug: slugSchema,
  summary: z.string().max(2000).nullable(),
  position: positionSchema,
  // 0/1/2 — enforced <= 2 (spec §3.1 "max depth 3"), see
  // `SECTION_DEPTH_EXCEEDED`.
  depth: z.number().int().min(0).max(2),
  // Materialised ancestor-chain path, e.g. "<rootId>/<childId>/...".
  path: z.string().min(1),
  isPublished: z.boolean(),
  publishedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type SectionResponse = z.infer<typeof sectionResponseSchema>;

// ─── GET /v1/subjects/:id/tree ───────────────────────────────────────────────
// "full authoring tree, one call" (spec §6.2) — every `Section` under the
// subject, nested by `parentId` into a `children` array. `z.lazy` breaks the
// otherwise-circular self-reference (mirrors `auth.ts`'s
// `loginResponseSchema`/`authMeResponseSchema` `z.lazy` precedent — see
// `../../apps/api/src/routes/openapi.ts`'s header for the one place a lazy
// schema needs a doc-generation-only workaround).

export interface SectionTreeNode extends SectionResponse {
  children: SectionTreeNode[];
}

export const sectionTreeNodeSchema: z.ZodType<SectionTreeNode> = sectionResponseSchema.extend({
  children: z.lazy(() => z.array(sectionTreeNodeSchema)),
});

export const subjectTreeResponseSchema = z.object({
  subjectId: idSchema,
  // Root-level sections (parentId null) with their full descendant chain
  // nested underneath — never a flat list a client would have to re-nest
  // itself.
  sections: z.array(sectionTreeNodeSchema),
});
export type SubjectTreeResponse = z.infer<typeof subjectTreeResponseSchema>;

// ─── POST /v1/sections/reorder response ──────────────────────────────────────
// The full, freshly dense-reindexed sibling set (not just an ack) so a
// caller can repaint its list from the response without a follow-up GET.

export const sectionReorderResponseSchema = z.object({
  items: z.array(sectionResponseSchema),
});
export type SectionReorderResponse = z.infer<typeof sectionReorderResponseSchema>;
